import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { authInstance } from '@/shared/api';
import { getMe, IUserDto } from '@/shared/api/account';
import { useNotifications } from '@/shared/ui/Notifications';

const PROFILE_QUERY_KEY = 'profile';
const BASE_URL_NOT_FOUND = 'API URL not configured yet';
type ProfileDto = IUserDto & {
  account_support_id?: string;
  account_type?: 'client' | 'partner' | null;
};

export const useProfile = () => {
  const exludedPaths = ['/auth', '/qr'];
  const { addNotification } = useNotifications();

  const queryFn = async () => {
    if (!authInstance.defaults.baseURL) {
      throw new Error(BASE_URL_NOT_FOUND);
    }

    return getMe();
  };

  const {
    data: profile,
    isError,
    error,
    ...other
  } = useQuery<ProfileDto>({
    queryKey: [PROFILE_QUERY_KEY],
    queryFn,
    refetchOnWindowFocus: !exludedPaths.some((path) =>
      window.location.pathname.includes(path)
    ),
    enabled: !window.location.pathname.includes('/qr'),
  });

  useEffect(() => {
    if (profile) {
      // PII: email/username в Sentry не отправляем — только стабильный id
      // (beforeSend-скраббер дополнительно редактирует event.user).
      Sentry.setUser({
        id: profile.id?.toString(),
        ...(profile.timezone && { timezone: profile.timezone }),
      });
    } else {
      // Сбрасываем информацию о пользователе при выходе
      Sentry.setUser(null);
    }
  }, [profile]);

  useEffect(() => {
    if (
      isError &&
      error.message !== BASE_URL_NOT_FOUND &&
      error.status !== 401
    ) {
      addNotification({
        type: 'error',
        title: error.message,
        caption: error.data?.details,
      });
    }
  }, [error, addNotification, isError]);

  return { profile, error, ...other };
};

export const useInvalidateProfileQuery = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [PROFILE_QUERY_KEY],
    });
};
